/**
 * Tenant Logo Fallback Utility
 *
 * Prevents print-js from hanging forever when a tenant's logo URL is broken
 * (common after the member.dekacare.id portal was retired). print-js polls
 * `image.complete && naturalWidth > 0` with no error handling — a broken
 * image means the print window never opens, even though the toast succeeds.
 *
 * See: node_modules/print-js/src/js/print.js loadIframeImage()
 */

export const FALLBACK_LOGO = '/logo-klinik-umum.jpg'

/**
 * URL patterns known to be unreachable. Add here as more retired endpoints
 * surface in tenant data.
 */
const RETIRED_URL_PATTERNS = [
  /member\.dekacare\.id/i,
  /member\.dekacare\.co\.id/i
]

/**
 * Returns a usable logo URL, swapping obviously dead references for the
 * bundled fallback. Does not perform network validation — use the
 * useSafeLogo composable for reactive preloading.
 *
 * @param {string|null|undefined} url
 * @returns {string}
 */
export function resolveLogoUrl(url) {
  if (!url || typeof url !== 'string') {
    return FALLBACK_LOGO
  }

  const trimmed = url.trim()
  if (!trimmed) {
    return FALLBACK_LOGO
  }

  for (const pattern of RETIRED_URL_PATTERNS) {
    if (pattern.test(trimmed)) {
      return FALLBACK_LOGO
    }
  }

  return trimmed
}

/**
 * @error handler for <img> tags. Swaps src to the fallback once. The guard
 * prevents an infinite error loop if the fallback itself somehow fails.
 *
 * Usage: <img :src="logoUrl" @error="onLogoError" />
 *
 * @param {Event} event
 */
export function onLogoError(event) {
  const target = event && event.target
  if (!target || target.dataset.fallbackApplied === '1') {
    return
  }
  target.dataset.fallbackApplied = '1'
  target.src = FALLBACK_LOGO
}

export default {
  FALLBACK_LOGO,
  resolveLogoUrl,
  onLogoError
}
